import React, { useEffect, useState } from "react";
import {
  Box,
  FormControl,
  FormGroup,
  Paper,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { IconHeader } from "../../../../Icon/IconHader/IconHeader";
import { updateStatusReport } from "../../../../../apis";
import { useParams } from "react-router";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";
import { EGCheckbox } from "../../../../MainComponents/Checkbox/EGCheckbox";

/**
 * ProcessTaskList Component
 * Displays checkboxes for the "process" status only
 * Editable only when the current report status is "process"
 */
export const ProcessTaskList = ({ reportDetail, onUpdate }) => {
  // Custom Hooks
  const [t] = useTranslation("report");
  const theme = useTheme();
  const { id } = useParams();
  const { username } = useSelector((state) => state.auth);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  // Initial states
  const [isLoading, setIsLoading] = useState(false);
  const [checkboxes, setCheckboxes] = useState([]);
  const [allChecked, setAllChecked] = useState(false);

  // Current report status
  const currentStatus = reportDetail?.status?.current;
  const isProcessStatus = currentStatus === "process";

  // Get the latest history entry for "process" status
  const latestHistoryEntry = reportDetail.status?.history
    ?.filter(
      (entry) =>
        entry.status === "process" && entry.sub_status?.length > 0
    )
    ?.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))[0];

  const normalizeKey = (k) => (k || "").toString().trim();

  // Get initial checkboxes for "process" status only
  const getInitialCheckboxes = () => {
    // Debug: Log the structure to understand the data
    console.log("🔍 reportDetail.status:", reportDetail?.status);
    console.log("🔍 sub_status structure:", reportDetail?.status?.sub_status);

    // Base: object with keys like new_0_... -> { key, checked, ... }
    const baseObj =
      reportDetail?.status?.sub_status?.[0]?.[0]?.process || {};

    console.log("🔍 baseObj for process:", baseObj);

    // Convert base to list (maintaining order from Object.values)
    const baseList = Object.values(baseObj).map((v, idx) => ({
      id: v.id ?? `base-${idx}`,
      key: v.key ?? `item-${idx}`,
      checked: !!v.checked,
      update_at: v.update_at ?? v.updated_at ?? "",
      updated_by: v.updated_by ?? "",
    }));

    // History (array) with the last time it was saved (may be empty)
    const historyList = latestHistoryEntry?.sub_status ?? [];

    // Map by normalized key to overwrite
    const historyMap = {};
    historyList.forEach((h) => {
      const k = normalizeKey(h.key);
      historyMap[k] = {
        checked:
          typeof h.checked === "boolean" ? h.checked : !!historyMap[k]?.checked,
        update_at: h.update_at ?? h.updated_at ?? "",
        updated_by: h.updated_by ?? "",
        id: h.id ?? null,
      };
    });

    const merged = baseList.map((item) => {
      const k = normalizeKey(item.key);
      if (historyMap[k]) {
        return {
          ...item,
          id: historyMap[k].id ?? item.id,
          checked: historyMap[k].checked,
          update_at: historyMap[k].update_at,
          updated_by: historyMap[k].updated_by,
        };
      }
      return item;
    });

    historyList.forEach((h, idx) => {
      const k = normalizeKey(h.key);
      const exists = merged.some((m) => normalizeKey(m.key) === k);
      if (!exists) {
        merged.push({
          id: h.id ?? `history-${idx}`,
          key: h.key,
          checked: !!h.checked,
          update_at: h.update_at ?? h.updated_at ?? "",
          updated_by: h.updated_by ?? "",
        });
      }
    });

    console.log("✅ Final merged checkboxes for process:", merged);
    return merged;
  };

  // Load checkboxes on startup or when reportDetail changes
  useEffect(() => {
    const initialCheckboxes = getInitialCheckboxes();
    console.log("📋 Setting checkboxes, count:", initialCheckboxes.length);
    setCheckboxes(initialCheckboxes);
    setAllChecked(
      initialCheckboxes.length === 0 ||
        initialCheckboxes.every((item) => item.checked)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportDetail]);

  // Synchronize `allChecked` when checkboxes change
  useEffect(() => {
    setAllChecked(
      checkboxes.length === 0 || checkboxes.every((item) => item.checked)
    );
  }, [checkboxes]);

  // Send data to backend
  const sendDataCheckboxes = async (updatedCheckboxes) => {
    setIsLoading(true);
    const updating = enqueueSnackbar(`${t("Care.updating")}...`, {
      variant: "info",
      persist: true,
    });

    try {
      const dataToSend = {
        report_id: id,
        status: "process",
        comment: "",
        message: "",
        checkboxes: updatedCheckboxes,
      };

      const resp = await updateStatusReport(dataToSend);

      if (resp?.message === "Updated Status") {
        enqueueSnackbar(t("Resolution.data_successfully_updated"), {
          variant: "success",
        });
        // Call onUpdate callback if provided
        if (onUpdate) {
          onUpdate();
        }
        return resp;
      } else {
        const msg = resp?.message || "Unexpected response";
        throw new Error(msg);
      }
    } catch (error) {
      console.error("❌ Error sending data:", error);
      throw error;
    } finally {
      closeSnackbar(updating);
      setIsLoading(false);
    }
  };

  // Handle individual checkbox changes
  const handleCheckboxChange = (idKey) => {
    if (!isProcessStatus) return;

    setCheckboxes((prev) => {
      const prevClone = prev.map((p) => ({ ...p }));

      const updatedCheckboxes = prev.map((item) => {
        if (item.key === idKey) {
          return {
            ...item,
            checked: !item.checked,
            update_at: dayjs().format("YYYY/MM/DD HH:mm:ss"),
            updated_by: username,
          };
        }
        return item;
      });

      // Optimistic update: update UI now and try to persist
      sendDataCheckboxes(updatedCheckboxes).catch((err) => {
        // Rollback if fails
        console.warn("Rollback check due to error:", err);
        setCheckboxes(prevClone);
      });

      return updatedCheckboxes;
    });
  };

  // Handle "Select/Deselect all"
  const handleSelectAll = () => {
    if (!isProcessStatus) return;

    setCheckboxes((prev) => {
      const prevClone = prev.map((p) => ({ ...p }));
      const newCheckedState = !allChecked;
      const updatedCheckboxes = prev.map((item) => ({
        ...item,
        checked: newCheckedState,
        update_at: dayjs().format("YYYY/MM/DD HH:mm:ss"),
        updated_by: username,
      }));

      sendDataCheckboxes(updatedCheckboxes).catch((err) => {
        console.warn("Rollback selectAll due to error:", err);
        setCheckboxes(prevClone);
        enqueueSnackbar(t("Resolution.error_updating") || "Error updating", {
          variant: "error",
        });
      });

      return updatedCheckboxes;
    });
  };

  // Don't render if no checkboxes exist for "process"
  console.log("🎯 Rendering ProcessTaskList, checkboxes.length:", checkboxes.length);

  if (checkboxes.length === 0) {
    console.log("⚠️ Not rendering: no checkboxes found for process status");
    return null;
  }

  return (
    <Paper
      elevation={0}
      className="dyBorder1 px-3 py-2"
      sx={{
        mt: 2,
        borderRadius: "6px",
        borderLeft: `6px solid ${
          isProcessStatus
            ? theme.palette.primary.main
            : theme.palette.grey[400]
        }`,
      }}
    >
      <FormControl component="fieldset" sx={{ width: "100%", mt: 0.5, px: 0.5 }}>
        <IconHeader
          title={`${t("Care.sub_status")} - ${t("status.process")}`}
          subtitle={
            !isProcessStatus &&
            t("Care.read_only_mode")
          }
        />

        {checkboxes.length > 1 && (
          <Box px={1}>
            <EGCheckbox
              label={
                allChecked ? t("Care.deselect_all") : t("Care.select_all")
              }
              checked={allChecked}
              onChange={handleSelectAll}
              disabled={isLoading || !isProcessStatus}
              color="primary"
              baseColor="secondary"
              checkedColorLabel="secondary"
              typographyProps={{ fontSize: 13 }}
            />
          </Box>
        )}

        <FormGroup
          sx={{
            display: "grid",
            px: 2,
            gridTemplateColumns: {
              xs: "1fr",
              lg: "1fr 1fr",
              xxl: "1fr 1fr 1fr",
            },
            gap: 0.5,
          }}
        >
          {checkboxes.map((task, i) => (
            <EGCheckbox
              label={task.key}
              key={`task-process-${i + 1}`}
              checked={task.checked}
              onChange={() => handleCheckboxChange(task.key)}
              disabled={isLoading || !isProcessStatus}
              color="primary"
              checkedColorLabel="secondary"
              baseColor="secondary"
              typographyProps={{ fontSize: 13 }}
            />
          ))}
        </FormGroup>
      </FormControl>
    </Paper>
  );
};
